// src/services/rfidService.js
import Vue from "vue";
import {EventBus} from '@/eventBus';

class RFIDService {
	constructor() {
		// RFID-related state
		this.ndef = null;
		this.abortController = null;
		this.isConnected = false;
		this.connectionPromise = null;
		this._onReading = this.onRfidRead.bind(this);
		this._onReadingError = this.onReadingErrorHandler.bind(this);

		// Initialize event bus for emitting RFID events
		this.eventBus = EventBus || new Vue();
	}

	doesDeviceHaveNFC() {
		try {
			return "NDEFReader" in window && typeof window.NDEFReader !== 'undefined';
		} catch {
			return false;
		}
	}

	async connectReader() {
		if (!this.doesDeviceHaveNFC()) {
			alert("This device does not support NFC. Please use Chrome on an Android Device.");
			return false
		}

		if (this.isConnected) {
			return true
		}

		if (this.connectionPromise) {
			return this.connectionPromise
		}

		const connectionPromise = this.startConnection()
		this.connectionPromise = connectionPromise

		try {
			return await connectionPromise
		} finally {
			if (this.connectionPromise === connectionPromise) {
				this.connectionPromise = null
			}
		}
	}

	async startConnection() {
		let abortController = null
		let ndef = null

		try {
			abortController = new AbortController();
			ndef = new window.NDEFReader();

			this.abortController = abortController;
			this.ndef = ndef;

			ndef.addEventListener("reading", this._onReading);
			ndef.addEventListener("readingerror", this._onReadingError);

			await ndef.scan({signal: abortController.signal});

			if (this.ndef !== ndef || abortController.signal.aborted) {
				return false
			}

			this.isConnected = true;
			this.eventBus.$emit(`rfid:connected`);
			return true
		} catch (error) {
			this.cleanupReader(ndef, abortController)

			if (error.name !== 'AbortError') {
				// eslint-disable-next-line no-console
				console.error('Unable to connect RFID reader', error)
				alert('Cannot read data from the RFID. Try again or return it to your Gate Supervisor or Champion.');
			}

			return false
		}
	}

	onReadingErrorHandler() {
		alert('Cannot read data from the RFID. Try again or return it to your Gate Supervisor or Champion.');
	}

	// Called when an RFID is read successfully
	// RFID is emitted as a Low to High 7 byte Hex string (14 chars) with the colons stripped.
	onRfidRead({serialNumber}) {
		// Strip colons
		const rfid = serialNumber.replace(/:/g, '');

		this.eventBus.$emit(`rfid:scanned`, {rfid: rfid});
	}

	disconnectReader() {
		this.isConnected = false
		this.connectionPromise = null
		this.cleanupReader(this.ndef, this.abortController)
	}

	cleanupReader(ndef, abortController) {
		if (ndef) {
			ndef.removeEventListener("reading", this._onReading);
			ndef.removeEventListener("readingerror", this._onReadingError);
		}

		if (abortController && !abortController.signal.aborted) {
			abortController.abort()
		}

		if (this.ndef === ndef) {
			this.ndef = null
		}

		if (this.abortController === abortController) {
			this.abortController = null
		}
	}

	// Subscribe to RFID events
	on(event, callback) {
		this.eventBus.$on(event, callback);
	}

	once(event, callback) {
		this.eventBus.$once(event, callback);
	}

	// Unsubscribe from RFID events
	off(event, callback) {
		this.eventBus.$off(event, callback);
	}
}

// Export singleton instance
const rfidService = new RFIDService();
export default rfidService;
